// src/encounter/lib/appointment-patient.ts
//
// PROD-2804. Who the encounter is FOR, when the page was opened without router state.
//
// `EncounterShellPage` takes its patient from three places, in order: the resolved encounter's
// `subject`, the tap-time nav context (Today / Calendar), and — added here — the appointment the
// route names. The first two are both absent on a deep link, a refresh or a bookmark to an
// appointment that has no encounter yet (still Booked, or Arrived but never opened from a list):
// `GetEncounterByEncounterId` 404s and there is no router state. The page then had no patient id,
// every patient read stayed disabled, and the rail rendered the mapper's empty sections as
// "No allergies recorded", "No medications recorded"… — an UNLOADED chart that read as an empty
// one, under a header that said only "Patient" (measured on dev, 1 Oct 2026).
//
// The appointment record already carries the patient. The page fetches it on every open
// (`useAppointmentStatus` → `GET /api/Appointment/GetById/{id}`) for the status pill and room, and
// its `participant[]` is the same `ParticipantResponse` the Today list reads the patient from
// (`Scrips.Core.Models.Scheduling.ParticipantResponse`: `Reference`, `ReferenceType`, `Patient`).
// No new endpoint.
//
// The participant rule matches `scheduling/lib/appointment-to-form-values.ts`: the patient is
// `referenceType` "Patient" for an adult and "RelatedPerson" for a guardian-managed minor, and the
// entry that CARRIES a `patient` node wins. The bare `reference` is trusted only on a "Patient"
// entry — on a "RelatedPerson" entry with no patient node it can name the guardian, and a chart
// read against the guardian's id would be worse than no chart.

/** The loose wire shape of one appointment participant (list row or GetById). */
export interface AppointmentParticipantWire {
  referenceType?: string | null;
  reference?: string | null;
  patient?: {
    id?: string | null;
    name?: string | null;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
  } | null;
}

/** The patient the appointment is for. Both fields optional; `id` is what the page needs. */
export interface AppointmentPatient {
  id?: string;
  /** Display name, when the participant carries one. Never a fabricated "Unknown Patient". */
  name?: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function asId(v: string | null | undefined): string | undefined {
  const s = v?.trim();
  return s && s !== EMPTY_GUID ? s : undefined;
}

const PATIENT_TYPES = ['patient', 'relatedperson'];

/**
 * The appointment's patient, or undefined when the participant list names none.
 */
export function patientFromAppointment(
  participant: AppointmentParticipantWire[] | null | undefined,
): AppointmentPatient | undefined {
  const list = participant ?? [];
  const typeOf = (p: AppointmentParticipantWire) => (p?.referenceType ?? '').toLowerCase();
  const entry =
    list.find((p) => PATIENT_TYPES.includes(typeOf(p)) && !!p?.patient) ??
    list.find((p) => typeOf(p) === 'patient');
  if (!entry) return undefined;
  const node = entry.patient ?? undefined;
  const id = asId(node?.id) ?? (typeOf(entry) === 'patient' ? asId(entry.reference) : undefined);
  const name = (
    node?.name?.trim() ||
    [node?.firstName, node?.middleName, node?.lastName].filter(Boolean).join(' ').trim()
  ) || undefined;
  if (!id && !name) return undefined;
  return { ...(id ? { id } : {}), ...(name ? { name } : {}) };
}

/**
 * Where the encounter's patient stands.
 *
 *  - `resolved` — a patient id is in hand; the patient reads can run.
 *  - `pending`  — no id yet, and a read that could supply one (the encounter by appointment, or the
 *                 appointment itself) is still in flight, retries included.
 *  - `failed`   — no id, and every read that could supply one has settled without one. The page
 *                 must say so ("Couldn't load this patient"), never render an empty chart.
 *
 * `isLoading` is TanStack's "first fetch in flight" — true through retries, false for a DISABLED
 * query, which is the right reading: the encounter query is disabled once an encounter id is known,
 * and a disabled query will never supply a subject.
 */
export type PatientResolution = 'resolved' | 'pending' | 'failed';

export function resolvePatientState(input: {
  patientId: string | null | undefined;
  encounterLoading?: boolean;
  appointmentLoading?: boolean;
}): PatientResolution {
  if (input.patientId) return 'resolved';
  if (input.encounterLoading || input.appointmentLoading) return 'pending';
  return 'failed';
}
